import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Signature, TypedDataEncoder, getBytes, hashMessage } from 'ethers';
import { LanguageSelect } from '../components/LanguageSelect';
import {
  getJSON,
  patchJSON,
  postJSON,
  type CatalogResponse,
  type CatalogToken,
  type MessageResponse,
  type UserProfile,
  type RipplePrepareResponse,
  type RippleSubmitResponse,
  type WalletDetail,
  type WatchedTokenBalancesResponse,
  type WatchedTokenBalanceEntry,
  type WatchedTokenMeta,
  type WatchedTokenRow,
  type WatchedTokensMetaResponse,
  userWalletApiBase,
} from '../lib/api';
import { EXPLORER_CHAIN_KEYS, explorerAddressUrl, explorerTxUrl } from '../lib/explorers';
import { appMainClass, narrowPanelError, pageWrapClass } from '../layout/page';
import {
  dangerSubmitInlineClass,
  inputFieldClass,
  outlineButtonBlockClass,
  primarySubmitButtonClass,
  selectFieldClass,
} from '../components/form/fieldClasses';
import { useWalletSessionUnlocked } from '../hooks/useWalletSessionUnlocked';
import { getMpcUnlock } from '../lib/mpcUnlockCache';
import { signDigestRawWithWalletMpc, signDigestWithWalletMpc } from '../lib/mpcSession';
import { sha256Utf8Hex } from '../lib/passwordWire';
import {
  approveWalletConnectSession,
  chainKeyForEip155,
  computeWalletConnectChainOffer,
  ensureWalletConnectReady,
  getWalletConnectSessionChains,
  onWalletConnectSessionProposal,
  onWalletConnectSessionRequest,
  pairWalletConnectUri,
  rejectWalletConnectSession,
  respondWalletConnectRequestError,
  respondWalletConnectRequestResult,
  tryHandleWalletConnectUtilityRequest,
  typedDataDomainMatchesRequestChain,
  type WalletConnectSessionProposal,
  type WalletConnectSessionRequest,
} from '../lib/walletConnect';

function catalogOptionKey(t: CatalogToken): string {
  return `${t.chain}\t${t.token_ref}`;
}

/** MPC returns bare hex (no 0x); ethers v6 Signature.from requires 0x-prefixed BigNumberish. */
function hex0x(h: string): string {
  const t = h.trim();
  return /^0x/i.test(t) ? t : `0x${t}`;
}

/**
 * Serialize MPC (r,s,v) for WalletConnect eth_sign / typed-data responses.
 * - Prefix r/s with 0x for ethers
 * - Normalize v∈{27..30} to legacy 27/28 (ethers rejects 29/30)
 */
function serializeMpcSigForWalletConnect(sig: { r_hex: string; s_hex: string; v: number }): string {
  let v = Number(sig.v);
  if (v >= 27 && v <= 30) {
    v = 27 + ((v - 27) & 1);
  } else if (v !== 0 && v !== 1) {
    throw new Error(`invalid signature v: ${String(sig.v)}`);
  }
  return Signature.from({ r: hex0x(sig.r_hex), s: hex0x(sig.s_hex), v }).serialized;
}

function watchRowKey(chain: string, tokenRef: string): string {
  return `${chain}\t${tokenRef}`;
}

function rowsToMeta(rows: WatchedTokenRow[]): WatchedTokenMeta[] {
  return rows.map((r) => ({
    chain: r.chain,
    token_ref: r.token_ref,
    symbol: r.symbol,
    name: r.name,
    decimals: r.decimals,
  }));
}

function mergeWatchedRows(
  meta: WatchedTokenMeta[],
  opts:
    | { phase: 'pending' }
    | { phase: 'ready'; balances: WatchedTokenBalanceEntry[] }
    | { phase: 'error'; message: string },
): WatchedTokenRow[] {
  if (opts.phase === 'pending') {
    return meta.map((t) => ({
      ...t,
      balance_formatted: '',
      balance_error: null,
      balance_pending: true,
    }));
  }
  if (opts.phase === 'error') {
    return meta.map((t) => ({
      ...t,
      balance_formatted: '',
      balance_error: opts.message,
      balance_pending: false,
    }));
  }
  const bMap = new Map(opts.balances.map((b) => [watchRowKey(b.chain, b.token_ref), b]));
  return meta.map((t) => {
    const b = bMap.get(watchRowKey(t.chain, t.token_ref));
    return {
      chain: t.chain,
      token_ref: t.token_ref,
      symbol: b?.symbol ?? t.symbol,
      name: b?.name ?? t.name,
      decimals: b?.decimals ?? t.decimals,
      balance_formatted: b?.balance_formatted ?? '',
      balance_error: b?.balance_error ?? null,
      balance_pending: false,
    };
  });
}

/** After GET /tokens, keep prior balance fields for rows that still exist; new rows start as pending. */
function mergeMetaPreserveBalances(meta: WatchedTokenMeta[], prev: WatchedTokenRow[]): WatchedTokenRow[] {
  const pmap = new Map(prev.map((r) => [watchRowKey(r.chain, r.token_ref), r]));
  return meta.map((m) => {
    const old = pmap.get(watchRowKey(m.chain, m.token_ref));
    if (old) {
      return {
        ...m,
        balance_formatted: old.balance_formatted,
        balance_error: old.balance_error,
        balance_pending: old.balance_pending ?? false,
      };
    }
    return { ...m, balance_formatted: '', balance_error: null, balance_pending: true };
  });
}

function applyBalancePatch(rows: WatchedTokenRow[], patch: WatchedTokenBalanceEntry[]): WatchedTokenRow[] {
  if (patch.length === 0) {
    return rows;
  }
  const bmap = new Map(patch.map((b) => [watchRowKey(b.chain, b.token_ref), b]));
  return rows.map((r) => {
    const b = bmap.get(watchRowKey(r.chain, r.token_ref));
    if (!b) {
      return r;
    }
    return {
      ...r,
      symbol: b.symbol ?? r.symbol,
      name: b.name ?? r.name,
      decimals: b.decimals ?? r.decimals,
      balance_formatted: b.balance_formatted,
      balance_error: b.balance_error,
      balance_pending: false,
    };
  });
}

async function postTokenBalancesRequest(
  base: string,
  tokens: WatchedTokenMeta[],
): Promise<{ ok: true; balances: WatchedTokenBalanceEntry[] } | { ok: false; error: string }> {
  if (tokens.length === 0) {
    return { ok: true, balances: [] };
  }
  const { res, data } = await postJSON<WatchedTokenBalancesResponse>(`${base}/tokens/balances`, {
    tokens: tokens.map((t) => ({ chain: t.chain, token_ref: t.token_ref })),
  });
  if (!res.ok) {
    return { ok: false, error: data.error || '' };
  }
  return { ok: true, balances: data.balances ?? [] };
}

const WALLET_CHAIN_KEYS = ['ethereum', 'sepolia', 'bsc', 'base', 'avax', 'polygon', 'arb', 'op'] as const;
type WalletViewType = 'evm' | 'ripple' | 'bitcoin';
type RippleAction = 'transfer' | 'set_regular_key' | 'disable_master' | null;

/** Returns lowercase 40-char hex without 0x, or null if invalid / zero address. */
function parseContractTokenRef(raw: string): string | null {
  const s = raw.trim().toLowerCase();
  const hex = s.startsWith('0x') ? s.slice(2) : s;
  if (!/^[0-9a-f]{40}$/.test(hex)) {
    return null;
  }
  if (/^0{40}$/.test(hex)) {
    return null;
  }
  return hex;
}

/** iOS-style list remove: solid red circle + white minus (jiggle / edit mode affordance). */
function IconRemoveCircle({ className }: { className?: string }) {
  return (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" aria-hidden>
      {/* System red ≈ iOS destructive / remove badge */}
      <circle cx="12" cy="12" r="10" fill="#FF3B30" />
      <path d="M8 12h8" stroke="white" strokeWidth="2.25" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function IconRefresh({ className, spinning }: { className?: string; spinning?: boolean }) {
  return (
    <svg
      className={`${className ?? ''} ${spinning ? 'animate-spin' : ''}`.trim()}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

export default function WalletDetailPage() {
  const { id } = useParams<{ id: string }>();
  const walletId = Number(id);
  const payUnlocked = useWalletSessionUnlocked(walletId);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [walletLoading, setWalletLoading] = useState(true);
  const [watchedListLoading, setWatchedListLoading] = useState(false);
  const [wallet, setWallet] = useState<WalletDetail | null>(null);
  const [catalog, setCatalog] = useState<CatalogToken[]>([]);
  const [watched, setWatched] = useState<WatchedTokenRow[]>([]);
  const [msg, setMsg] = useState('');
  const [msgOk, setMsgOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  const [rippleRefreshing, setRippleRefreshing] = useState(false);
  const [rippleBusy, setRippleBusy] = useState(false);
  const [rippleAction, setRippleAction] = useState<RippleAction>(null);
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [walletType, setWalletType] = useState<WalletViewType>('evm');
  const [renameName, setRenameName] = useState('');
  const [renameEditing, setRenameEditing] = useState(false);
  const [renameBusy, setRenameBusy] = useState(false);
  const [ripplePayPwd, setRipplePayPwd] = useState('');
  const [rippleMfa, setRippleMfa] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [rippleSourceAddress, setRippleSourceAddress] = useState('');
  const [rippleDestination, setRippleDestination] = useState('');
  const [rippleAmount, setRippleAmount] = useState('');
  const [rippleDestinationTag, setRippleDestinationTag] = useState('');
  const [rippleRegularKey, setRippleRegularKey] = useState('rop1ff87wjMxFPns2JbAYuihT4CEidzP3');
  const [rippleTxHash, setRippleTxHash] = useState('');
  const [wcQrOpen, setWcQrOpen] = useState(false);
  const [wcUriInput, setWcUriInput] = useState('');
  const [wcScanErr, setWcScanErr] = useState('');
  const [wcProposal, setWcProposal] = useState<WalletConnectSessionProposal | null>(null);
  const [wcRequest, setWcRequest] = useState<WalletConnectSessionRequest | null>(null);
  const [wcActionBusy, setWcActionBusy] = useState(false);
  const [wcPayPwd, setWcPayPwd] = useState('');
  const [wcMfa, setWcMfa] = useState('');
  const [wcActiveChain, setWcActiveChain] = useState('eip155:1');
  const [wcOfferedChains, setWcOfferedChains] = useState<string[]>([]);
  const [wcMissingRequiredChains, setWcMissingRequiredChains] = useState<string[]>([]);
  const evmRef = useRef('');
  const [addChain, setAddChain] = useState<string>('ethereum');
  const [tokenInput, setTokenInput] = useState('');
  const [pickedTokenRef, setPickedTokenRef] = useState<string | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const comboRef = useRef<HTMLDivElement>(null);
  const watchedFetchGen = useRef(0);
  const balanceRefreshGen = useRef(0);
  const watchedRef = useRef<WatchedTokenRow[]>([]);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const qrScannerRef = useRef<Html5Qrcode | null>(null);
  const qrRegionId = `walletconnect-qr-region-${walletId}`;

  async function stopQrScanner(): Promise<void> {
    const cur = qrScannerRef.current;
    qrScannerRef.current = null;
    if (!cur) {
      return;
    }
    try {
      await cur.stop();
    } catch {
      // Scanner may already be stopped.
    }
    try {
      if (document.getElementById(qrRegionId)) {
        await cur.clear();
      }
    } catch {
      // DOM node may already be unmounted.
    }
  }

  async function closeWcQrModal(): Promise<void> {
    await stopQrScanner();
    setWcQrOpen(false);
  }

  useEffect(() => {
    watchedRef.current = watched;
  }, [watched]);

  const base = Number.isFinite(walletId) && walletId > 0 ? userWalletApiBase(walletId) : '';

  const watchedSet = useMemo(() => {
    const s = new Set<string>();
    for (const w of watched) {
      s.add(`${w.chain}\t${w.token_ref}`);
    }
    return s;
  }, [watched]);

  const addableOptions = useMemo(() => {
    return catalog.filter((c) => !watchedSet.has(catalogOptionKey(c)));
  }, [catalog, watchedSet]);

  const addableOnChain = useMemo(
    () => addableOptions.filter((c) => c.chain === addChain),
    [addableOptions, addChain],
  );

  const filteredCatalog = useMemo(() => {
    const q = tokenInput.trim().toLowerCase();
    if (!q || pickedTokenRef !== null) {
      return addableOnChain;
    }
    return addableOnChain.filter(
      (c) =>
        c.symbol.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.token_ref.toLowerCase().includes(q),
    );
  }, [addableOnChain, tokenInput, pickedTokenRef]);

  const canSubmitAdd = useMemo(() => {
    if (pickedTokenRef !== null) {
      return addableOnChain.some((c) => c.token_ref === pickedTokenRef);
    }
    const t = tokenInput.trim();
    if (t === '') {
      return true;
    }
    return parseContractTokenRef(t) !== null;
  }, [pickedTokenRef, addableOnChain, tokenInput]);

  useEffect(() => {
    if (!catalogOpen) {
      return;
    }
    const onDoc = (e: MouseEvent) => {
      if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
        setCatalogOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [catalogOpen]);

  type WatchedSyncOpts =
    | { kind: 'full'; isCancelled?: () => boolean }
    | { kind: 'meta-only'; isCancelled?: () => boolean }
    | { kind: 'meta-and-new-balances'; keysBefore: Set<string>; isCancelled?: () => boolean };

  const syncWatched = useCallback(
    async (opts: WatchedSyncOpts) => {
      const isCancelled = opts.isCancelled ?? (() => false);
      if (!base) {
        return;
      }
      const gen = ++watchedFetchGen.current;
      const showListLoading = opts.kind === 'full';
      if (showListLoading) {
        setWatchedListLoading(true);
      }
      try {
        const tr = await getJSON<WatchedTokensMetaResponse>(`${base}/tokens`);
        if (isCancelled() || gen !== watchedFetchGen.current) {
          return;
        }
        if (!tr.res.ok) {
          if (showListLoading) {
            setWatched([]);
          }
          setMsgOk(false);
          setMsg(tr.data.error || t('common.error'));
          return;
        }
        const meta = tr.data.tokens ?? [];

        if (opts.kind === 'full') {
          setWatched(mergeWatchedRows(meta, { phase: 'pending' }));
          const br = await postTokenBalancesRequest(base, meta);
          if (isCancelled() || gen !== watchedFetchGen.current) {
            return;
          }
          if (br.ok) {
            setWatched(mergeWatchedRows(meta, { phase: 'ready', balances: br.balances }));
          } else {
            setWatched(
              mergeWatchedRows(meta, {
                phase: 'error',
                message: br.error || t('common.error'),
              }),
            );
          }
          return;
        }

        if (opts.kind === 'meta-only') {
          setWatched(mergeMetaPreserveBalances(meta, watchedRef.current));
          return;
        }

        const nextRows = mergeMetaPreserveBalances(meta, watchedRef.current);
        setWatched(nextRows);
        const newTokens = meta.filter((m) => !opts.keysBefore.has(watchRowKey(m.chain, m.token_ref)));
        const br = await postTokenBalancesRequest(base, newTokens);
        if (isCancelled() || gen !== watchedFetchGen.current) {
          return;
        }
        if (!br.ok) {
          setMsgOk(false);
          setMsg(br.error || t('common.error'));
          return;
        }
        setWatched(applyBalancePatch(nextRows, br.balances));
      } finally {
        if (showListLoading && gen === watchedFetchGen.current && !isCancelled()) {
          setWatchedListLoading(false);
        }
      }
    },
    [base, t],
  );

  const refreshBalances = useCallback(async () => {
    if (!base) {
      return;
    }
    const gen = ++balanceRefreshGen.current;
    setBalanceRefreshing(true);
    try {
      const meta = rowsToMeta(watchedRef.current);
      const br = await postTokenBalancesRequest(base, meta);
      if (gen !== balanceRefreshGen.current) {
        return;
      }
      if (!br.ok) {
        setMsgOk(false);
        setMsg(br.error || t('common.error'));
        return;
      }
      setWatched((prev) => {
        if (prev.length === 0) {
          return prev;
        }
        return applyBalancePatch(prev, br.balances);
      });
    } finally {
      if (gen === balanceRefreshGen.current) {
        setBalanceRefreshing(false);
      }
    }
  }, [base, t]);

  useEffect(() => {
    if (!base) {
      return;
    }
    let cancelled = false;
    const isCancelled = () => cancelled;

    void (async () => {
      setWalletLoading(true);
      setMsg('');
      const [wr, pr] = await Promise.all([
        getJSON<WalletDetail>(base),
        getJSON<UserProfile>('/api/user/profile'),
      ]);
      if (isCancelled()) {
        return;
      }
      if (wr.res.status === 401 || pr.res.status === 401) {
        navigate('/', { replace: true });
        return;
      }
      if (wr.res.status === 403 || pr.res.status === 403) {
        navigate('/home', { replace: true });
        return;
      }
      setMfaEnabled(pr.res.ok && !!pr.data.mfa_enabled);
      if (!wr.res.ok) {
        setWallet(null);
        setMsg(wr.data.error || t('common.error'));
        setMsgOk(false);
        setWalletLoading(false);
        return;
      }
      setWallet(wr.data);
      setWalletLoading(false);

      await Promise.all([
        (async () => {
          const cr = await getJSON<CatalogResponse>(`${base}/tokens/catalog`);
          if (isCancelled()) {
            return;
          }
          setCatalog(cr.res.ok ? (cr.data.tokens ?? []) : []);
          if (!cr.res.ok) {
            setMsgOk(false);
            setMsg(cr.data.error || t('common.error'));
          }
        })(),
        syncWatched({ kind: 'full', isCancelled }),
      ]);
    })();

    return () => {
      cancelled = true;
    };
  }, [base, syncWatched, navigate, t, i18n.language]);

  async function onAddToken(e: FormEvent) {
    e.preventDefault();
    if (!base || !canSubmitAdd) {
      return;
    }

    let token_ref: string;
    if (pickedTokenRef !== null) {
      const row = addableOnChain.find((c) => c.token_ref === pickedTokenRef);
      if (!row) {
        setMsgOk(false);
        setMsg(t('wallet.pickedTokenStale'));
        return;
      }
      token_ref = row.token_ref === 'native' ? '' : `0x${row.token_ref}`;
    } else {
      const trimmed = tokenInput.trim();
      if (trimmed === '') {
        token_ref = '';
      } else {
        const hex = parseContractTokenRef(trimmed);
        if (!hex) {
          setMsgOk(false);
          setMsg(t('wallet.tokenInputInvalid'));
          return;
        }
        token_ref = `0x${hex}`;
      }
    }

    setBusy(true);
    setMsg('');
    const keysBefore = new Set(watchedRef.current.map((r) => watchRowKey(r.chain, r.token_ref)));
    try {
      const { res, data } = await postJSON<MessageResponse>(`${base}/tokens`, {
        chain: addChain,
        token_ref,
      });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || t('wallet.tokenAdded'));
      setTokenInput('');
      setPickedTokenRef(null);
      setCatalogOpen(false);
      await syncWatched({ kind: 'meta-and-new-balances', keysBefore });
    } finally {
      setBusy(false);
    }
  }

  function onChainChange(next: string) {
    setAddChain(next);
    setTokenInput('');
    setPickedTokenRef(null);
    setCatalogOpen(false);
  }

  function onTokenInputChange(v: string) {
    setTokenInput(v);
    setPickedTokenRef(null);
  }

  function pickCatalogRow(c: CatalogToken) {
    setPickedTokenRef(c.token_ref);
    setTokenInput(`${c.symbol} — ${c.name}`);
    setCatalogOpen(false);
  }

  async function onRemove(chain: string, tokenRef: string) {
    if (!base) {
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const { res, data } = await postJSON<MessageResponse>(`${base}/tokens/unwatch`, {
        chain,
        token_ref: tokenRef,
      });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || t('wallet.tokenRemoved'));
      await syncWatched({ kind: 'meta-only' });
    } finally {
      setBusy(false);
    }
  }

  const evm = wallet?.evm_address?.trim() ?? '';
  const ripple = wallet?.ripple_address?.trim() ?? '';
  const xrpBalance = wallet?.xrp_balance?.trim() ?? '';
  const rippleRegularKeyOnLedger = wallet?.ripple_regular_key?.trim() ?? '';
  const rippleDisableMaster = wallet?.ripple_disable_master === true;

  useEffect(() => {
    if (!wallet) {
      return;
    }
    setRenameName(wallet.name ?? '');
    setRenameEditing(false);
  }, [wallet]);

  useEffect(() => {
    if (!renameEditing) {
      return;
    }
    renameInputRef.current?.focus();
    renameInputRef.current?.select();
  }, [renameEditing]);

  useEffect(() => {
    if (!wallet) {
      return;
    }
    const hasEvm = !!wallet.evm_address?.trim();
    const hasRipple = !!wallet.ripple_address?.trim();
    if (!hasEvm && hasRipple) {
      setWalletType('ripple');
    }
  }, [wallet]);

  async function submitRenameWallet() {
    if (!base || !wallet) {
      return;
    }
    const name = renameName.trim();
    if (!name) {
      setMsgOk(false);
      setMsg(t('wallet.renameRequired'));
      return;
    }
    setRenameBusy(true);
    setMsg('');
    try {
      const { res, data } = await patchJSON<MessageResponse>(`${base}/name`, { name });
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setWallet((prev) => (prev ? { ...prev, name } : prev));
      setRenameEditing(false);
      setMsgOk(true);
      setMsg(data.message || t('wallet.renameSuccess'));
    } finally {
      setRenameBusy(false);
    }
  }

  async function onRenameBlur() {
    if (!renameEditing || renameBusy || !wallet) {
      return;
    }
    const next = renameName.trim();
    if (!next || next === wallet.name) {
      setRenameName(wallet.name);
      setRenameEditing(false);
      return;
    }
    await submitRenameWallet();
  }

  async function refreshRippleBalance() {
    if (!base) {
      return;
    }
    setRippleRefreshing(true);
    try {
      const wr = await getJSON<WalletDetail>(base);
      if (wr.res.ok) {
        setWallet((prev) =>
          prev
            ? {
                ...prev,
                ripple_address: wr.data.ripple_address,
                xrp_balance: wr.data.xrp_balance,
                ripple_regular_key: wr.data.ripple_regular_key,
                ripple_disable_master: wr.data.ripple_disable_master,
              }
            : wr.data,
        );
      } else {
        setMsgOk(false);
        setMsg(wr.data.error || t('common.error'));
      }
    } finally {
      setRippleRefreshing(false);
    }
  }

  function parseOptionalUInt32(raw: string): number | null {
    const v = raw.trim();
    if (!v) {
      return null;
    }
    if (!/^\d+$/.test(v)) {
      throw new Error(t('wallet.ripple.tagInvalid'));
    }
    const n = Number(v);
    if (!Number.isSafeInteger(n) || n < 0 || n > 0xffffffff) {
      throw new Error(t('wallet.ripple.tagInvalid'));
    }
    return n;
  }

  async function submitRippleAction(
    action: Exclude<RippleAction, null>,
    preparePath: string,
    prepareBody: Record<string, unknown>,
    submitPath: string,
  ) {
    if (!base) {
      return;
    }
    const payPwd = ripplePayPwd.trim();
    let payWire: string;
    if (payPwd) {
      payWire = await sha256Utf8Hex(payPwd);
    } else {
      const unlock = await getMpcUnlock(walletId);
      if (!unlock?.payPwdWire) {
        setMsgOk(false);
        setMsg(t('wallet.ripple.payPwdRequired'));
        return;
      }
      payWire = unlock.payPwdWire;
    }
    setRippleBusy(true);
    setRippleAction(action);
    setRippleTxHash('');
    setMsg('');
    try {
      const mfaPart = mfaEnabled ? { mfa_code: rippleMfa.trim() } : {};
      const { res: prepRes, data: prep } = await postJSON<RipplePrepareResponse>(preparePath, {
        ...prepareBody,
        payment_password: payWire,
        ...mfaPart,
      });
      if (!prepRes.ok || !prep.signing_digest_hex || !prep.tx_json) {
        throw new Error(prep.error || t('common.error'));
      }
      const sig = await signDigestRawWithWalletMpc(walletId, prep.signing_digest_hex, payPwd, {
        commonErrorMessage: t('common.error'),
        sessionMissingMessage: t('transfer.sessionMissing'),
        sessionInvalidMessage: t('transfer.sessionInvalid'),
        mfaCode: mfaEnabled ? rippleMfa.trim() : undefined,
      });
      const { res: submitRes, data: submit } = await postJSON<RippleSubmitResponse>(submitPath, {
        payment_password: payWire,
        r_hex: sig.r_hex,
        s_hex: sig.s_hex,
        tx_json: prep.tx_json,
        ...mfaPart,
      });
      if (!submitRes.ok) {
        throw new Error(submit.error || t('common.error'));
      }
      setMsgOk(true);
      setMsg(submit.message || t('wallet.ripple.submitted'));
      setRippleTxHash(submit.tx_hash?.trim() ?? '');
      setRipplePayPwd('');
      setRippleMfa('');
      await refreshRippleBalance();
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
    } finally {
      setRippleBusy(false);
      setRippleAction(null);
    }
  }

  async function onRippleTransfer(e: FormEvent) {
    e.preventDefault();
    const destination = rippleDestination.trim();
    if (!destination) {
      setMsgOk(false);
      setMsg(t('wallet.ripple.destinationRequired'));
      return;
    }
    const amount = rippleAmount.trim();
    if (!amount) {
      setMsgOk(false);
      setMsg(t('wallet.ripple.amountRequired'));
      return;
    }
    let destinationTag: number | undefined;
    try {
      const parsed = parseOptionalUInt32(rippleDestinationTag);
      if (parsed !== null) {
        destinationTag = parsed;
      }
    } catch (err) {
      setMsgOk(false);
      setMsg(String(err));
      return;
    }
    const body: Record<string, unknown> = { destination, amount_xrp: amount };
    if (rippleSourceAddress.trim()) {
      body.source_address = rippleSourceAddress.trim();
    }
    if (destinationTag !== undefined) {
      body.destination_tag = destinationTag;
    }
    await submitRippleAction('transfer', `${base}/ripple/payment/prepare`, body, `${base}/ripple/payment/submit`);
  }

  async function onRippleSetRegularKey(e: FormEvent) {
    e.preventDefault();
    const regularKey = rippleRegularKey.trim();
    if (!regularKey) {
      setMsgOk(false);
      setMsg(t('wallet.ripple.regularKeyRequired'));
      return;
    }
    await submitRippleAction(
      'set_regular_key',
      `${base}/ripple/set-regular-key/prepare`,
      { regular_key: regularKey },
      `${base}/ripple/set-regular-key/submit`,
    );
  }

  async function onRippleDisableMaster(e: FormEvent) {
    e.preventDefault();
    await submitRippleAction(
      'disable_master',
      `${base}/ripple/disable-master/prepare`,
      {},
      `${base}/ripple/disable-master/submit`,
    );
  }

  function isWalletConnectUri(raw: string): boolean {
    return /^wc:/i.test(raw.trim());
  }

  async function submitWalletConnectUri(raw: string): Promise<boolean> {
    const uri = raw.trim();
    if (!isWalletConnectUri(uri)) {
      setWcScanErr(t('wallet.qr.walletConnectInvalid'));
      return false;
    }
    try {
      await pairWalletConnectUri(uri);
      setWcUriInput('');
      setWcScanErr('');
      await stopQrScanner();
      setWcQrOpen(false);
      setMsgOk(true);
      setMsg(t('wallet.qr.walletConnectDetected'));
      return true;
    } catch (err) {
      const msg = String(err);
      if (msg.includes('WALLETCONNECT_PROJECT_ID_MISSING')) {
        setWcScanErr(t('wallet.qr.walletConnectProjectMissing'));
      } else {
        setWcScanErr(t('wallet.qr.walletConnectPairFailed'));
      }
      return false;
    }
  }

  function wcReqPreview(r: WalletConnectSessionRequest): string {
    try {
      return JSON.stringify(r.params, null, 2);
    } catch {
      return String(r.params);
    }
  }

  function extractMsgParam(method: string, params: unknown): { address?: string; message: string } | null {
    if (!Array.isArray(params) || params.length < 2) {
      return null;
    }
    const a0 = String(params[0] ?? '');
    const a1 = String(params[1] ?? '');
    const isAddr0 = /^0x[0-9a-fA-F]{40}$/.test(a0);
    const isAddr1 = /^0x[0-9a-fA-F]{40}$/.test(a1);
    if (method === 'personal_sign') {
      if (isAddr1) {
        return { address: a1, message: a0 };
      }
      if (isAddr0) {
        return { address: a0, message: a1 };
      }
      return { message: a0 };
    }
    if (method === 'eth_sign') {
      if (isAddr0) {
        return { address: a0, message: a1 };
      }
      return { message: a1 };
    }
    return null;
  }

  function parseTypedDataPayload(params: unknown): { address?: string; typedData: Record<string, unknown> } | null {
    if (!Array.isArray(params) || params.length < 2) {
      return null;
    }
    const addr = String(params[0] ?? '');
    const raw = params[1];
    const parsed = typeof raw === 'string' ? (JSON.parse(raw) as Record<string, unknown>) : (raw as Record<string, unknown>);
    return { address: /^0x[0-9a-fA-F]{40}$/.test(addr) ? addr : undefined, typedData: parsed };
  }

  function openWalletConnectProposal(p: WalletConnectSessionProposal): void {
    const offer = computeWalletConnectChainOffer(p);
    setWcProposal(p);
    setWcOfferedChains(offer.offeredChains);
    setWcMissingRequiredChains(offer.missingRequiredChains);
    setWcActiveChain(
      offer.offeredChains.includes(offer.defaultActiveChain)
        ? offer.defaultActiveChain
        : offer.offeredChains[0] ?? offer.defaultActiveChain,
    );
  }

  function formatWcChainLabel(caip: string): string {
    const key = chainKeyForEip155(caip);
    if (key) {
      return `${t(`wallet.chain.${key}`)} (${caip})`;
    }
    return caip;
  }

  async function onApproveWalletConnectProposal() {
    if (!wcProposal || !evm) {
      return;
    }
    if (wcMissingRequiredChains.length > 0 || wcOfferedChains.length === 0) {
      setMsgOk(false);
      setMsg(t('wallet.qr.walletConnectNoSupportedChains'));
      return;
    }
    const active = wcOfferedChains.includes(wcActiveChain) ? wcActiveChain : wcOfferedChains[0];
    setWcActionBusy(true);
    try {
      await approveWalletConnectSession(wcProposal.id, wcProposal.raw, evm, wcOfferedChains, active);
      setWcProposal(null);
      setMsgOk(true);
      setMsg(t('wallet.qr.walletConnectSessionApproved'));
    } catch {
      setMsgOk(false);
      setMsg(t('wallet.qr.walletConnectSessionApproveFailed'));
    } finally {
      setWcActionBusy(false);
    }
  }

  async function onRejectWalletConnectProposal() {
    if (!wcProposal) {
      return;
    }
    setWcActionBusy(true);
    try {
      await rejectWalletConnectSession(wcProposal.id, 'User rejected session');
      setWcProposal(null);
      setMsgOk(true);
      setMsg(t('wallet.qr.walletConnectSessionRejected'));
    } catch {
      setMsgOk(false);
      setMsg(t('wallet.qr.walletConnectSessionApproveFailed'));
    } finally {
      setWcActionBusy(false);
    }
  }

  async function onRejectWalletConnectRequest() {
    if (!wcRequest) {
      return;
    }
    setWcActionBusy(true);
    try {
      await respondWalletConnectRequestError(wcRequest.topic, wcRequest.id, 4001, 'User rejected');
      setWcRequest(null);
      setWcPayPwd('');
      setWcMfa('');
      setMsgOk(true);
      setMsg(t('wallet.qr.walletConnectRequestRejected'));
    } catch {
      setMsgOk(false);
      setMsg(t('wallet.qr.walletConnectRequestApproveFailed'));
    } finally {
      setWcActionBusy(false);
    }
  }

  async function onApproveWalletConnectRequest() {
    if (!wcRequest || !evm) {
      return;
    }
    setWcActionBusy(true);
    let stage = 'init';
    const debugBase = {
      method: wcRequest.method,
      chainId: wcRequest.chainId,
      topic: wcRequest.topic,
      requestId: wcRequest.id,
      walletId,
      walletEvm: evm,
    };
    try {
      const method = wcRequest.method;
      if (method === 'personal_sign' || method === 'eth_sign') {
        stage = 'parse_personal_sign';
        const info = extractMsgParam(method, wcRequest.params);
        if (!info) {
          throw new Error('bad params');
        }
        if (info.address && info.address.toLowerCase() !== evm.toLowerCase()) {
          throw new Error(`address mismatch: req=${info.address} wallet=${evm}`);
        }
        stage = 'hash_personal_sign';
        const msg = info.message.startsWith('0x') ? getBytes(info.message) : info.message;
        const digest = hashMessage(msg);
        stage = 'mpc_sign';
        console.info('[wc-sign]', { ...debugBase, stage, digest });
        const sig = await signDigestWithWalletMpc(walletId, digest, wcPayPwd, {
          commonErrorMessage: t('common.error'),
          sessionMissingMessage: t('transfer.sessionMissing'),
          sessionInvalidMessage: t('transfer.sessionInvalid'),
          mfaCode: mfaEnabled ? wcMfa.trim() : undefined,
        });
        stage = 'serialize_signature';
        console.info('[wc-sign]', {
          ...debugBase,
          stage,
          r_hex: sig.r_hex,
          s_hex: sig.s_hex,
          v: sig.v,
          vType: typeof sig.v,
        });
        const serialized = serializeMpcSigForWalletConnect(sig);
        stage = 'wc_respond';
        console.info('[wc-sign]', { ...debugBase, stage, serializedLen: serialized.length });
        await respondWalletConnectRequestResult(wcRequest.topic, wcRequest.id, serialized);
      } else if (
        method === 'eth_signTypedData' ||
        method === 'eth_signTypedData_v3' ||
        method === 'eth_signTypedData_v4'
      ) {
        stage = 'parse_typed_data';
        const td = parseTypedDataPayload(wcRequest.params);
        if (!td) {
          throw new Error('bad typed data');
        }
        if (td.address && td.address.toLowerCase() !== evm.toLowerCase()) {
          throw new Error(`address mismatch: req=${td.address} wallet=${evm}`);
        }
        const domain = (td.typedData.domain ?? {}) as Record<string, unknown>;
        stage = 'check_chain';
        if (!typedDataDomainMatchesRequestChain(domain, wcRequest.chainId)) {
          await respondWalletConnectRequestError(
            wcRequest.topic,
            wcRequest.id,
            5000,
            'Typed data domain.chainId does not match request chain',
          );
          throw new Error(
            `chain_mismatch: domain.chainId=${String(domain.chainId)} request=${wcRequest.chainId}`,
          );
        }
        stage = 'hash_typed_data';
        const types = { ...((td.typedData.types ?? {}) as Record<string, Array<{ name: string; type: string }>>) };
        const message = (td.typedData.message ?? {}) as Record<string, unknown>;
        delete (types as Record<string, unknown>).EIP712Domain;
        const digest = TypedDataEncoder.hash(domain, types, message);
        stage = 'mpc_sign';
        console.info('[wc-sign]', {
          ...debugBase,
          stage,
          digest,
          primaryType: td.typedData.primaryType,
          domainChainId: domain.chainId,
          reqAddress: td.address,
        });
        const sig = await signDigestWithWalletMpc(walletId, digest, wcPayPwd, {
          commonErrorMessage: t('common.error'),
          sessionMissingMessage: t('transfer.sessionMissing'),
          sessionInvalidMessage: t('transfer.sessionInvalid'),
          mfaCode: mfaEnabled ? wcMfa.trim() : undefined,
        });
        stage = 'serialize_signature';
        console.info('[wc-sign]', {
          ...debugBase,
          stage,
          r_hex: sig.r_hex,
          s_hex: sig.s_hex,
          v: sig.v,
          vType: typeof sig.v,
        });
        const serialized = serializeMpcSigForWalletConnect(sig);
        stage = 'wc_respond';
        console.info('[wc-sign]', { ...debugBase, stage, serializedLen: serialized.length });
        await respondWalletConnectRequestResult(wcRequest.topic, wcRequest.id, serialized);
      } else {
        stage = 'unsupported_method';
        await respondWalletConnectRequestError(wcRequest.topic, wcRequest.id, -32601, `Unsupported method: ${method}`);
        throw new Error('unsupported');
      }
      setWcRequest(null);
      setWcPayPwd('');
      setWcMfa('');
      setMsgOk(true);
      setMsg(t('wallet.qr.walletConnectRequestApproved'));
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      console.error('[wc-sign] failed', { ...debugBase, stage, err, detail });
      if (detail.includes('unsupported')) {
        setMsgOk(false);
        setMsg(t('wallet.qr.walletConnectMethodUnsupported'));
      } else if (detail.includes('chain_mismatch')) {
        setMsgOk(false);
        setMsg(`${t('wallet.qr.walletConnectChainMismatch')} [${stage}] ${detail}`);
      } else {
        setMsgOk(false);
        setMsg(`${t('wallet.qr.walletConnectRequestApproveFailed')} [${stage}] ${detail}`);
      }
    } finally {
      setWcActionBusy(false);
    }
  }

  useEffect(() => {
    evmRef.current = evm;
  }, [evm]);

  useEffect(() => {
    void ensureWalletConnectReady().catch(() => undefined);
    const offProposal = onWalletConnectSessionProposal((p) => openWalletConnectProposal(p));
    const offRequest = onWalletConnectSessionRequest((r) => {
      void (async () => {
        const address = evmRef.current;
        if (!address) {
          setWcRequest(r);
          return;
        }
        try {
          const sessionChains = await getWalletConnectSessionChains(r.topic);
          const result = await tryHandleWalletConnectUtilityRequest(r, address, sessionChains);
          if (result.handled) {
            if (result.notice === 'switched') {
              setMsgOk(true);
              setMsg(t('wallet.qr.walletConnectChainSwitched'));
            } else if (result.notice === 'unsupported_chain') {
              setMsgOk(false);
              setMsg(t('wallet.qr.walletConnectChainUnsupported'));
            }
            return;
          }
        } catch {
          // Fall through to interactive modal for sign / unknown methods.
        }
        setWcRequest(r);
      })();
    });
    return () => {
      offProposal();
      offRequest();
    };
  }, [t]);

  useEffect(() => {
    if (!wcQrOpen) {
      return;
    }
    let cancelled = false;
    setWcScanErr('');

    void (async () => {
      await stopQrScanner();
      if (cancelled || !document.getElementById(qrRegionId)) {
        return;
      }
      const scanner = new Html5Qrcode(qrRegionId, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      qrScannerRef.current = scanner;
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 220 },
          (decodedText) => {
            if (cancelled) {
              return;
            }
            void (async () => {
              if (await submitWalletConnectUri(decodedText)) {
                cancelled = true;
              }
            })();
          },
          () => undefined,
        );
      } catch {
        if (!cancelled) {
          setWcScanErr(t('wallet.qr.cameraStartFailed'));
        }
      }
    })();

    return () => {
      cancelled = true;
      void stopQrScanner();
    };
  }, [wcQrOpen, qrRegionId]);

  if (!Number.isFinite(walletId) || walletId <= 0) {
    return (
      <div className={pageWrapClass}>
        <p className="text-sm text-red-600">{t('wallet.invalidId')}</p>
        <Link to="/home" className="mt-4 inline-block text-blue-600">
          {t('wallet.backHome')}
        </Link>
      </div>
    );
  }

  if (walletLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-slate-100 text-slate-600">{t('home.loading')}</div>
    );
  }

  if (!wallet) {
    return (
      <div className={pageWrapClass}>
        <main className={narrowPanelError}>
          <p className="text-sm text-red-600">{msg || t('common.error')}</p>
          <Link to="/home" className="mt-4 inline-block text-sm text-blue-600 hover:underline">
            {t('wallet.backHome')}
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className={pageWrapClass}>
      <main className={appMainClass}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/home" className="text-sm font-medium text-blue-600 hover:underline">
              ← {t('wallet.backHome')}
            </Link>
            <Link
              to={`/home/swap?wallet=${walletId}`}
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              {t('swap.nav')}
            </Link>
            <Link
              to={`/home/transfer?wallet=${walletId}`}
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              {t('transfer.nav')}
            </Link>
            <Link
              to={`/home/proxy-transfer?wallet=${walletId}`}
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              {t('proxyTransfer.nav')}
            </Link>
          </div>
          <LanguageSelect />
        </div>

        {msg ? (
          <div
            role="status"
            className={`rounded-xl border px-4 py-3 text-sm font-medium ${
              msgOk
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {msg}
          </div>
        ) : null}

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {renameEditing ? (
            <input
              ref={renameInputRef}
              type="text"
              className={`${inputFieldClass} max-w-xl text-2xl font-semibold text-slate-900`}
              placeholder={t('wallet.renamePlaceholder')}
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onBlur={() => void onRenameBlur()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void submitRenameWallet();
                }
                if (e.key === 'Escape' && wallet) {
                  e.preventDefault();
                  setRenameName(wallet.name);
                  setRenameEditing(false);
                }
              }}
              disabled={renameBusy}
            />
          ) : (
            <div className="inline-flex max-w-full items-center gap-2">
              <h1
                className="max-w-full cursor-text truncate border-b border-dashed border-slate-300 text-2xl font-semibold text-slate-900 transition-colors hover:border-blue-400 hover:text-blue-700"
                title={t('wallet.renameDoubleClickHint')}
                onDoubleClick={() => {
                  if (!renameBusy) {
                    setRenameEditing(true);
                  }
                }}
              >
                {wallet.name}
              </h1>
              <span
                className="select-none text-sm text-slate-400"
                title={t('wallet.renameDoubleClickHint')}
                aria-hidden
              >
                ✎
              </span>
              <span className="text-xs text-slate-500">{t('wallet.renameDoubleClickHint')}</span>
            </div>
          )}
          <div className="mt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('wallet.type')}</p>
            <div className="mt-2 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 text-sm">
              {(['evm', 'ripple', 'bitcoin'] as WalletViewType[]).map((tp) => (
                <button
                  key={tp}
                  type="button"
                  onClick={() => setWalletType(tp)}
                  className={`rounded-md px-3 py-1.5 ${
                    walletType === tp
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  {t(`wallet.type.${tp}`)}
                </button>
              ))}
            </div>
          </div>
          {walletType === 'evm' ? (
          <div className="mt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('wallet.evmAddress')}</p>
            {evm ? (
              <button
                type="button"
                className="mt-1 w-full break-all text-left font-mono text-sm text-blue-700 underline decoration-blue-400 hover:text-blue-900"
                onClick={() => setExplorerOpen(true)}
              >
                {evm}
              </button>
            ) : (
              <p className="mt-1 text-sm text-slate-500">{t('home.walletNoEvmAddr')}</p>
            )}
            <button
              type="button"
              className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setWcQrOpen(true);
                setWcScanErr('');
              }}
            >
              {t('wallet.qr.scanWalletConnect')}
            </button>
          </div>
          ) : walletType === 'ripple' ? (
            <div className="mt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('wallet.rippleAddress')}</p>
              {ripple ? (
                <a
                  href={explorerAddressUrl('ripple', ripple) ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block w-full break-all text-left font-mono text-sm text-blue-700 underline decoration-blue-400 hover:text-blue-900"
                >
                  {ripple}
                </a>
              ) : (
                <p className="mt-1 text-sm text-slate-500">{t('wallet.rippleUnavailable')}</p>
              )}
              <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-500">{t('wallet.xrpBalance')}</p>
              <div className="mt-1 flex items-center gap-2">
                <p className="font-mono text-sm text-slate-900">{xrpBalance || '—'}</p>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  onClick={() => void refreshRippleBalance()}
                  disabled={rippleRefreshing}
                >
                  {rippleRefreshing ? t('home.loading') : t('home.refresh')}
                </button>
              </div>
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    {t('wallet.ripple.disableMasterKey')}
                  </span>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      rippleDisableMaster ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'
                    }`}
                  >
                    {rippleDisableMaster ? t('common.yes') : t('common.no')}
                  </span>
                </div>
                {rippleDisableMaster ? (
                  <div className="mt-2 flex items-start gap-2 text-sm">
                    <span className="shrink-0 text-slate-500">{t('wallet.ripple.regularKeyOnLedger')}:</span>
                    {rippleRegularKeyOnLedger ? (
                      <a
                        href={explorerAddressUrl('ripple', rippleRegularKeyOnLedger) ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="min-w-0 break-all font-mono text-blue-700 underline decoration-blue-400 hover:text-blue-900"
                      >
                        {rippleRegularKeyOnLedger}
                      </a>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </div>
                ) : null}
              </div>
              <div className="mt-6 space-y-6">
                <form className="space-y-2 rounded-xl border border-slate-200 p-3" onSubmit={(e) => void onRippleTransfer(e)}>
                  <h3 className="text-sm font-semibold text-slate-900">{t('wallet.ripple.transferTitle')}</h3>
                  <label className="block">
                    <span className="text-xs text-slate-600">{t('wallet.ripple.sourceAddress')}</span>
                    <input
                      type="text"
                      className={inputFieldClass}
                      value={rippleSourceAddress}
                      onChange={(e) => setRippleSourceAddress(e.target.value)}
                      placeholder={t('wallet.ripple.sourceAddressPh')}
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-600">{t('wallet.ripple.destination')}</span>
                    <input
                      type="text"
                      className={inputFieldClass}
                      value={rippleDestination}
                      onChange={(e) => setRippleDestination(e.target.value)}
                      placeholder="r..."
                    />
                  </label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-xs text-slate-600">{t('wallet.ripple.amountXrp')}</span>
                      <input
                        type="text"
                        className={inputFieldClass}
                        value={rippleAmount}
                        onChange={(e) => setRippleAmount(e.target.value)}
                        placeholder="0.1"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-slate-600">{t('wallet.ripple.destinationTag')}</span>
                      <input
                        type="text"
                        className={inputFieldClass}
                        value={rippleDestinationTag}
                        onChange={(e) => setRippleDestinationTag(e.target.value)}
                        placeholder={t('wallet.ripple.optional')}
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-xs text-slate-600">{t('wallet.ripple.paymentPassword')}</span>
                    <input
                      type="password"
                      required={!payUnlocked}
                      className={inputFieldClass}
                      value={ripplePayPwd}
                      onChange={(e) => setRipplePayPwd(e.target.value)}
                      placeholder={payUnlocked ? t('transfer.payPwdUnlockHint') : undefined}
                    />
                  </label>
                  {payUnlocked ? (
                    <p className="text-xs text-slate-500">{t('transfer.payPwdUnlockHint')}</p>
                  ) : null}
                  {mfaEnabled ? (
                    <label className="block">
                      <span className="text-xs text-slate-600">{t('mfa.code')}</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className={inputFieldClass}
                        value={rippleMfa}
                        onChange={(e) => setRippleMfa(e.target.value)}
                      />
                    </label>
                  ) : null}
                  <button
                    type="submit"
                    disabled={rippleBusy || !ripple}
                    className={primarySubmitButtonClass}
                  >
                    {rippleBusy && rippleAction === 'transfer' ? t('wallet.ripple.submitting') : t('wallet.ripple.transferSubmit')}
                  </button>
                </form>

                <form className="space-y-2 rounded-xl border border-slate-200 p-3" onSubmit={(e) => void onRippleSetRegularKey(e)}>
                  <h3 className="text-sm font-semibold text-slate-900">{t('wallet.ripple.setRegularKeyTitle')}</h3>
                  <label className="block">
                    <span className="text-xs text-slate-600">{t('wallet.ripple.regularKey')}</span>
                    <input
                      type="text"
                      className={inputFieldClass}
                      value={rippleRegularKey}
                      onChange={(e) => setRippleRegularKey(e.target.value)}
                      placeholder="r..."
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-slate-600">{t('wallet.ripple.paymentPassword')}</span>
                    <input
                      type="password"
                      required={!payUnlocked}
                      className={inputFieldClass}
                      value={ripplePayPwd}
                      onChange={(e) => setRipplePayPwd(e.target.value)}
                      placeholder={payUnlocked ? t('transfer.payPwdUnlockHint') : undefined}
                    />
                  </label>
                  {payUnlocked ? (
                    <p className="text-xs text-slate-500">{t('transfer.payPwdUnlockHint')}</p>
                  ) : null}
                  {mfaEnabled ? (
                    <label className="block">
                      <span className="text-xs text-slate-600">{t('mfa.code')}</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className={inputFieldClass}
                        value={rippleMfa}
                        onChange={(e) => setRippleMfa(e.target.value)}
                      />
                    </label>
                  ) : null}
                  <button
                    type="submit"
                    disabled={rippleBusy || !ripple}
                    className={primarySubmitButtonClass}
                  >
                    {rippleBusy && rippleAction === 'set_regular_key' ? t('wallet.ripple.submitting') : t('wallet.ripple.setRegularKeySubmit')}
                  </button>
                </form>

                <form className="space-y-2 rounded-xl border border-slate-200 p-3" onSubmit={(e) => void onRippleDisableMaster(e)}>
                  <h3 className="text-sm font-semibold text-slate-900">{t('wallet.ripple.disableMasterTitle')}</h3>
                  <p className="text-xs text-slate-600">{t('wallet.ripple.disableMasterHint')}</p>
                  <label className="block">
                    <span className="text-xs text-slate-600">{t('wallet.ripple.paymentPassword')}</span>
                    <input
                      type="password"
                      required={!payUnlocked}
                      className={inputFieldClass}
                      value={ripplePayPwd}
                      onChange={(e) => setRipplePayPwd(e.target.value)}
                      placeholder={payUnlocked ? t('transfer.payPwdUnlockHint') : undefined}
                    />
                  </label>
                  {payUnlocked ? (
                    <p className="text-xs text-slate-500">{t('transfer.payPwdUnlockHint')}</p>
                  ) : null}
                  {mfaEnabled ? (
                    <label className="block">
                      <span className="text-xs text-slate-600">{t('mfa.code')}</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        className={inputFieldClass}
                        value={rippleMfa}
                        onChange={(e) => setRippleMfa(e.target.value)}
                      />
                    </label>
                  ) : null}
                  <button
                    type="submit"
                    disabled={rippleBusy || !ripple}
                    className={dangerSubmitInlineClass}
                  >
                    {rippleBusy && rippleAction === 'disable_master' ? t('wallet.ripple.submitting') : t('wallet.ripple.disableMasterSubmit')}
                  </button>
                </form>
              </div>
              {rippleTxHash ? (
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                  <span className="text-emerald-900">{t('wallet.ripple.txHash')}:</span>{' '}
                  <a
                    href={explorerTxUrl('ripple', rippleTxHash) ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all font-mono text-blue-700 underline decoration-blue-400 hover:text-blue-900"
                  >
                    {rippleTxHash}
                  </a>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="mt-4 text-sm text-slate-500">{t('wallet.bitcoinComingSoon')}</div>
          )}
        </section>

        {walletType === 'evm' ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">{t('wallet.watchedTokens')}</h2>
          <p className="mt-1 text-sm text-slate-600">{t('wallet.watchedHint')}</p>

          <form className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end" onSubmit={(e) => void onAddToken(e)}>
            <div className="min-w-0 sm:w-44">
              <label htmlFor="add-chain" className="block text-xs font-medium text-slate-600">
                {t('wallet.contractChainLabel')}
              </label>
              <select
                id="add-chain"
                className={selectFieldClass}
                value={addChain}
                onChange={(e) => onChainChange(e.target.value)}
              >
                {WALLET_CHAIN_KEYS.map((ch) => (
                  <option key={ch} value={ch}>
                    {t(`wallet.chain.${ch}`)}
                  </option>
                ))}
              </select>
            </div>
            <div ref={comboRef} className="relative min-w-0 flex-1 sm:min-w-[220px]">
              <label htmlFor="token-or-contract" className="block text-xs font-medium text-slate-600">
                {t('wallet.tokenOrContractLabel')}
              </label>
              <div className="mt-1 flex rounded-lg border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-blue-500/30">
                <input
                  id="token-or-contract"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={t('wallet.tokenComboboxPh')}
                  className="min-w-0 flex-1 rounded-l-lg border-0 bg-transparent px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
                  value={tokenInput}
                  onChange={(e) => onTokenInputChange(e.target.value)}
                  onFocus={() => setCatalogOpen(true)}
                />
                <button
                  type="button"
                  className="shrink-0 rounded-r-lg border-l border-slate-200 px-3 text-slate-600 hover:bg-slate-50"
                  aria-expanded={catalogOpen}
                  aria-label={t('wallet.openTokenCatalog')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setCatalogOpen((o) => !o)}
                >
                  ▾
                </button>
              </div>
              {catalogOpen ? (
                <ul
                  className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                  role="listbox"
                >
                  {addableOnChain.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-slate-500">{t('wallet.catalogEmptyForChain')}</li>
                  ) : filteredCatalog.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-slate-500">{t('wallet.catalogNoMatch')}</li>
                  ) : (
                    filteredCatalog.map((c) => (
                      <li key={catalogOptionKey(c)} role="option">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickCatalogRow(c)}
                        >
                          <span className="font-medium">{c.symbol}</span>
                          <span className="text-slate-500"> — {c.name}</span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </div>
            <button
              type="submit"
              disabled={busy || !canSubmitAdd}
              className={`${primarySubmitButtonClass} shrink-0`}
            >
              {t('wallet.addToken')}
            </button>
          </form>

          <div className="mt-6 rounded-xl border border-slate-100">
            <table className="w-full table-fixed text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="min-w-0 px-2 py-2 pl-3 font-medium text-slate-700 sm:px-3">
                    {t('wallet.colTokenChain')}
                  </th>
                  <th className="w-[32%] px-2 py-2 text-right font-medium text-slate-700 sm:w-auto sm:px-3 sm:text-left">
                    <span className="inline-flex items-center gap-1.5">
                      {t('wallet.colBalance')}
                      <button
                        type="button"
                        disabled={balanceRefreshing || watched.length === 0 || !base}
                        title={t('wallet.refreshBalances')}
                        aria-label={t('wallet.refreshBalances')}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none disabled:opacity-40"
                        onClick={() => void refreshBalances()}
                      >
                        <IconRefresh spinning={balanceRefreshing} />
                      </button>
                    </span>
                  </th>
                  <th className="w-11 px-0 py-2 pr-1 text-right font-medium text-slate-700 sm:w-14 sm:px-3">
                    <span className="sr-only">{t('wallet.colActionSr')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {watchedListLoading && watched.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-slate-500">
                      {t('wallet.watchedListLoading')}
                    </td>
                  </tr>
                ) : watched.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-slate-500">
                      {t('wallet.noTokensYet')}
                    </td>
                  </tr>
                ) : (
                  watched.map((row) => (
                    <tr key={`${row.chain}-${row.token_ref}`} className="border-b border-slate-100 last:border-b-0">
                      <td className="min-w-0 py-2 pl-3 pr-1 align-top sm:px-3">
                        <div className="break-words">
                          <Link
                            to={`/home/wallet/${walletId}/token/${encodeURIComponent(row.chain)}/${encodeURIComponent(row.token_ref)}`}
                            state={{ row }}
                            className="font-semibold text-blue-700 underline decoration-blue-400 hover:text-blue-900"
                          >
                            {row.symbol}
                          </Link>
                          <span className="text-slate-400"> · </span>
                          <span className="text-slate-600">{t(`wallet.chain.${row.chain}`)}</span>
                        </div>
                        <div className="mt-0.5 break-words text-xs leading-snug text-slate-500">{row.name}</div>
                      </td>
                      <td className="px-2 py-2 text-right align-top font-mono text-xs text-slate-800 sm:px-3 sm:text-left sm:text-sm">
                        {row.balance_pending ? (
                          <span className="text-slate-400">{t('wallet.balanceLoading')}</span>
                        ) : row.balance_error ? (
                          <span className="text-amber-800" title={row.balance_error}>
                            —
                          </span>
                        ) : (
                          row.balance_formatted
                        )}
                      </td>
                      <td className="py-1.5 pr-1 text-right align-top sm:py-2 sm:pr-3">
                        <button
                          type="button"
                          disabled={busy}
                          title={t('wallet.remove')}
                          aria-label={t('wallet.remove')}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-opacity hover:opacity-90 active:scale-95 disabled:opacity-40 disabled:active:scale-100"
                          onClick={() => void onRemove(row.chain, row.token_ref)}
                        >
                          <IconRemoveCircle />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
        ) : null}
      </main>

      {walletType === 'evm' && explorerOpen && evm ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="presentation"
          onClick={() => setExplorerOpen(false)}
        >
          <div
            className="max-h-[90dvh] w-full max-w-md overflow-auto rounded-2xl bg-white p-6 shadow-xl"
            role="dialog"
            aria-labelledby="explorer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="explorer-title" className="text-lg font-semibold text-slate-900">
              {t('wallet.explorerTitle')}
            </h3>
            <p className="mt-1 text-sm text-slate-600">{t('wallet.explorerHint')}</p>
            <ul className="mt-4 grid gap-2">
              {EXPLORER_CHAIN_KEYS.map((ch) => {
                const href = explorerAddressUrl(ch, evm);
                if (!href) {
                  return null;
                }
                return (
                  <li key={ch}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg border border-slate-200 px-4 py-3 text-sm font-medium text-blue-700 hover:bg-slate-50"
                    >
                      {t(`wallet.chain.${ch}`)}
                    </a>
                  </li>
                );
              })}
            </ul>
            <button type="button" className={`mt-6 ${outlineButtonBlockClass}`} onClick={() => setExplorerOpen(false)}>
              {t('wallet.close')}
            </button>
          </div>
        </div>
      ) : null}
      {walletType === 'evm' && wcQrOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="presentation"
          onClick={() => void closeWcQrModal()}
        >
          <div
            className="max-h-[90dvh] w-full max-w-md overflow-auto rounded-2xl bg-white p-6 shadow-xl"
            role="dialog"
            aria-labelledby="walletconnect-qr-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="walletconnect-qr-title" className="text-lg font-semibold text-slate-900">
              {t('wallet.qr.scanWalletConnect')}
            </h3>
            <p className="mt-1 text-sm text-slate-600">{t('wallet.qr.scanHint')}</p>
            <div id={qrRegionId} className="mt-4 min-h-[260px] rounded-xl border border-slate-200 bg-slate-50 p-2" />
            <form
              className="mt-4 space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                void submitWalletConnectUri(wcUriInput);
              }}
            >
              <label className="block text-xs font-medium text-slate-600">{t('wallet.qr.manualInput')}</label>
              <input
                type="text"
                className={inputFieldClass}
                placeholder="wc:..."
                value={wcUriInput}
                onChange={(e) => setWcUriInput(e.target.value)}
              />
              <button type="submit" className={primarySubmitButtonClass}>
                {t('wallet.qr.confirmWalletConnect')}
              </button>
            </form>
            {wcScanErr ? <p className="mt-3 text-sm text-red-600">{wcScanErr}</p> : null}
            <button type="button" className={`mt-4 ${outlineButtonBlockClass}`} onClick={() => void closeWcQrModal()}>
              {t('wallet.close')}
            </button>
          </div>
        </div>
      ) : null}
      {wcProposal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">{t('wallet.qr.walletConnectSessionTitle')}</h3>
            <p className="mt-2 text-sm text-slate-600">{t('wallet.qr.walletConnectSessionHint')}</p>
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <p><span className="font-medium">{t('wallet.qr.walletConnectDapp')}:</span> {wcProposal.proposerName || '—'}</p>
              <p className="mt-1 break-all"><span className="font-medium">URL:</span> {wcProposal.proposerUrl || '—'}</p>
              <p className="mt-1 break-all"><span className="font-medium">{t('wallet.qr.walletConnectMethods')}:</span> {Object.values(wcProposal.requiredNamespaces).flatMap((n) => n.methods ?? []).join(', ') || '—'}</p>
              <p className="mt-1 break-all">
                <span className="font-medium">{t('wallet.qr.walletConnectChains')}:</span>{' '}
                {wcOfferedChains.map(formatWcChainLabel).join(', ') || '—'}
              </p>
              {wcMissingRequiredChains.length > 0 ? (
                <p className="mt-2 text-xs text-red-600">
                  {t('wallet.qr.walletConnectNoSupportedChains')}: {wcMissingRequiredChains.join(', ')}
                </p>
              ) : null}
            </div>
            {wcOfferedChains.length > 0 ? (
              <label className="mt-3 block">
                <span className="text-xs text-slate-600">{t('wallet.qr.walletConnectActiveChain')}</span>
                <select
                  className={`${selectFieldClass} mt-1`}
                  value={wcActiveChain}
                  onChange={(e) => setWcActiveChain(e.target.value)}
                >
                  {wcOfferedChains.map((caip) => (
                    <option key={caip} value={caip}>
                      {formatWcChainLabel(caip)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">{t('wallet.qr.walletConnectActiveChainHint')}</p>
              </label>
            ) : null}
            <div className="mt-4 flex gap-2">
              <button type="button" disabled={wcActionBusy} className={dangerSubmitInlineClass} onClick={() => void onRejectWalletConnectProposal()}>
                {t('wallet.qr.reject')}
              </button>
              <button
                type="button"
                disabled={wcActionBusy || wcOfferedChains.length === 0 || wcMissingRequiredChains.length > 0}
                className={primarySubmitButtonClass}
                onClick={() => void onApproveWalletConnectProposal()}
              >
                {t('wallet.qr.approve')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {wcRequest ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">{t('wallet.qr.walletConnectRequestTitle')}</h3>
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <p><span className="font-medium">Method:</span> {wcRequest.method}</p>
              <p className="mt-1"><span className="font-medium">Chain:</span> {wcRequest.chainId}</p>
            </div>
            <label className="mt-3 block">
              <span className="text-xs text-slate-600">{t('wallet.qr.walletConnectRequestPayload')}</span>
              <textarea className={`${inputFieldClass} mt-1 min-h-36 font-mono text-xs`} value={wcReqPreview(wcRequest)} readOnly />
            </label>
            <label className="mt-3 block">
              <span className="text-xs text-slate-600">{t('wallet.ripple.paymentPassword')}</span>
              <input
                type="password"
                className={inputFieldClass}
                required={!payUnlocked}
                value={wcPayPwd}
                onChange={(e) => setWcPayPwd(e.target.value)}
                placeholder={payUnlocked ? t('transfer.payPwdUnlockHint') : undefined}
              />
            </label>
            {mfaEnabled ? (
              <label className="mt-3 block">
                <span className="text-xs text-slate-600">{t('mfa.code')}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className={inputFieldClass}
                  value={wcMfa}
                  onChange={(e) => setWcMfa(e.target.value)}
                />
              </label>
            ) : null}
            <div className="mt-4 flex gap-2">
              <button type="button" disabled={wcActionBusy} className={dangerSubmitInlineClass} onClick={() => void onRejectWalletConnectRequest()}>
                {t('wallet.qr.reject')}
              </button>
              <button type="button" disabled={wcActionBusy} className={primarySubmitButtonClass} onClick={() => void onApproveWalletConnectRequest()}>
                {t('wallet.qr.approveAndSign')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
